import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { AddressSuggestionsService } from '@app/services/address-suggestions.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import * as formControls from '@app/shared/utils/form-controls';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
    selector: 'app-address-group',
    templateUrl: './address-group.component.html',
    styleUrls: ['./address-group.component.scss'],
    providers: [AddressSuggestionsService]
})
export class AddressGroupComponent implements OnInit {

    @Input() addressType: string;
    @Input() form: UntypedFormGroup;
    @Input() set disabled(isDisabled: boolean) {
        this.setLine1ControlToDisable(isDisabled);
    }
    @Output() onNzAddressSelected = new EventEmitter();
    suggestions$: BehaviorSubject<any>;
    addressDetailSub$: Subscription;

    constructor(
        public addressService: AddressSuggestionsService
    ) { }

    ngOnInit() {
        this.suggestions$ = this.addressService.suggestions$;
        this.addressDetailSub$ = this.addressService.detail$
            .subscribe(handleAddressDetailSub.bind(this));
        // disable line2, postCode etc. since they will be derived from the nzPost address
        this.setDerivedControlsToDisable(true);
    }

    getFriendlyFieldName(fieldName) {
        return formControls.CheckOutFormControls[fieldName];
    }

    public setSelectedNzPostAddress(nzPostAddress) {
        this.patchFromNzPostAddress(nzPostAddress);
        this.addressService.clearData();
        this.onNzAddressSelected.emit(nzPostAddress);
    }

    onInput(q: string): void {
    // always clear chosen address when input changes
        this.handleAddressLine1Clear();
        if (q.length < 3) {
            return;
        }
        const nzPostAddressType = this.addressType === 'physical' ? 'Physical'
            : this.addressType === 'postal' ? 'Postal'
                : 'All';
        this.addressService.getSuggesstions(q, nzPostAddressType);
    }

    onSelectionChange(event: MatAutocompleteSelectedEvent) {
        const nzPostAddressType = this.addressType === 'physical' ? 'Physical'
            : this.addressType === 'postal' ? 'Postal'
                : 'All';
        this.addressService.getDetail(event.option.value['DPID'], nzPostAddressType);
    }

    setDerivedControlsToDisable(flag) {
        if (flag) {
            this.form.get(this.addressType + 'Address.line2').disable();
            this.form.get(this.addressType + 'Address.townOrCity').disable();
            this.form.get(this.addressType + 'Address.postCode').disable();
            this.form.get(this.addressType + 'Address.province').disable();
        } else {
            this.form.get(this.addressType + 'Address.line2').enable();
            this.form.get(this.addressType + 'Address.townOrCity').enable();
            this.form.get(this.addressType + 'Address.postCode').enable();
            this.form.get(this.addressType + 'Address.province').enable();
            // don't enable country select - we must use "New Zealand"
        }
    }

    setLine1ControlToDisable(flag) {
        if (flag) {
            this.form.get(this.addressType + 'Address.line1').disable();
        } else {
            this.form.get(this.addressType + 'Address.line1').enable();
        }
    }

    handleAddressLine1Clear() {
        this.form.patchValue({ [this.addressType + 'Address']: { line2: '', townOrCity: '', postCode: '', nzPostDpId: '' } });
        this.addressService.clearData();
        this.onNzAddressSelected.emit(null);
    }

    /**
   * Checks if the address is still required (not provided).
   */
    showRequiredError() {
        const nzPostDpIdControl = this.form.get(`${this.addressType}Address.nzPostDpId`);
        const line1Control = this.form.get(`${this.addressType}Address.line1`);
        const isMissingDpId = nzPostDpIdControl.getError('required');
        const isTouched = line1Control.touched;
        const isEnabled = line1Control.enabled;
        return isMissingDpId && isTouched && isEnabled;
    }

    patchFromNzPostAddress(nzPostAddress) {
        if (!!nzPostAddress) {
            const line1 = nzPostAddress.AddressLine1;
            const line2 = nzPostAddress.Physical === 'Y' && nzPostAddress.Suburb !== nzPostAddress.CityTown ? nzPostAddress.Suburb
                : nzPostAddress.Physical === 'Y' ? '' : nzPostAddress.AddressLine2;
            const townOrCity = !!nzPostAddress.CityTown ? nzPostAddress.CityTown : nzPostAddress.MailTown;
            const postCode = nzPostAddress.Postcode;
            const nzPostDpId = nzPostAddress.DPID;
            // fields are expanded for readability
            this.form.patchValue({ [this.addressType + 'Address']: { line1, line2, townOrCity, postCode, nzPostDpId } });
        } else {
            this.form.patchValue({
                [this.addressType + 'Address']: { line1: '', line2: '', townOrCity: '', postCode: '', nzPostDpId: '' }
            });
        }
    }

    displayLine1Selection(suggestion: any): string {
        if (typeof suggestion === 'string') {
            return suggestion;
        } else if (suggestion && suggestion.FullAddress) {
            return suggestion.FullAddress;
        } else {
            return '';
        }
    }

}


const handleAddressDetailSub = function (details) {
    // if there is no detail do nothing
    if (!details || !details[0]) {
        return;
    }

    const [nzPostAddress] = details;
    this.setSelectedNzPostAddress(nzPostAddress);
};
